import { z } from 'zod';
import { brandChannelKindSchema } from './brand-channel-kind.js';
import { brandContentPlanCadenceSchema, brandContentPlanPillarSchema, } from './brand-content-plan.js';
// [12.2L-173] Channel takeover proposal — spec extension on §0b.
//
// Before Mag actually starts publishing on a brand's channel, she
// produces a written proposal: how she'll sound, how she'll look,
// the 3-5 pillars she'll thread through, the cadence, and a 4-6
// post narrative arc to kick things off. The founder reads the
// proposal and approves / discards. Approval is what flips
// brand_channels.operatedByMag to true — granting Meta OAuth scopes
// on its own is no longer treated as consent.
//
// Two modes branch on what Mag finds:
//   - 'build' : the channel is empty or near-empty. Mag proposes a
//               debut arc — origin → belief → proof → call — to
//               introduce the brand to the world deliberately.
//   - 'continue': the channel already has a voice. Mag identifies
//               what that voice is and proposes how she'll extend it
//               (not replace it), with a continuation arc.
export const channelTakeoverHistoryReadSchema = z.enum(['no_posts', 'sparse', 'has_voice']);
export const channelTakeoverModeSchema = z.enum(['build', 'continue']);
export const channelTakeoverStatusSchema = z.enum([
    'proposed',
    'approved',
    'discarded',
    'superseded',
]);
/** One position in the narrative arc Mag proposes. For 'build' this
 *  reads as a debut sequence (origin → POV → proof → call to follow);
 *  for 'continue' it riffs on the existing brand thread. */
export const channelTakeoverArcPostSchema = z.object({
    /** 1-indexed position in the arc. */
    index: z.number().int().min(1).max(8),
    /** One-line title of the post angle ("the origin", "what we
     *  refuse to do", "the receipt from a founder we helped"). */
    angle: z.string().min(1).max(120),
    /** Concrete copy + visual sketch — what the post might look like.
     *  Body text or a short shot list — Mag's first read, not a draft. */
    sketch: z.string().min(1).max(800),
    /** Why this post at this position in the arc — the strategic
     *  intent. Helps the founder approve / redirect with knowledge of
     *  why each beat exists. */
    intent: z.string().min(1).max(280),
});
/** What Mag will do first after approval — a small set of bullets.
 *  Reads as a punch list so the founder knows what to expect in the
 *  first week, not just "Mag will figure it out." */
export const channelTakeoverFirstStepSchema = z.object({
    label: z.string().min(1).max(80),
    description: z.string().min(1).max(280),
});
export const channelTakeoverProposalSchema = z.object({
    channelKind: brandChannelKindSchema,
    /** Build (debut) vs. continue (extend existing voice). Picked by
     *  the agent based on the historyRead signal — explicit in the
     *  payload so the UI can render the right framing. */
    mode: channelTakeoverModeSchema,
    /** What Mag saw on the channel when she looked: nothing, a few
     *  posts without much pattern, or a recognizable voice. Drives
     *  the mode + a small explainer line in the UI. */
    historyRead: channelTakeoverHistoryReadSchema,
    /** 1-2 sentence Mag-voice summary of the whole plan. Goes at the
     *  top of the proposal — the elevator pitch for what she's about
     *  to do. */
    summary: z.string().min(1).max(500),
    /** How Mag will sound — tone, register, recurring devices. */
    voiceDirection: z.string().min(1).max(600),
    /** What the visuals will lean on — palette pulls, motif use,
     *  composition principles. */
    visualDirection: z.string().min(1).max(600),
    /** Content pillars — same shape as BrandContentPlan so a takeover
     *  proposal can graduate into a real plan after approval. Required
     *  3-5 here so the founder reads a deliberate spread, not a
     *  laundry list. */
    pillars: z.array(brandContentPlanPillarSchema).min(3).max(5),
    /** Cadence Mag proposes. Same shape the content-plan agent uses. */
    cadence: brandContentPlanCadenceSchema,
    /** The narrative arc — 4-6 posts that tell a story when read in
     *  sequence. The MVP of "do you want me to build this" — when the
     *  founder approves, this is what Mag drafts first. */
    narrativeArc: z.array(channelTakeoverArcPostSchema).min(4).max(6),
    /** First-week punch list. Small set of concrete actions Mag will
     *  take immediately after approval. */
    firstSteps: z.array(channelTakeoverFirstStepSchema).min(2).max(4),
});
/** Envelope persisted on brand_channels.takeover_proposal. The
 *  payload is the proposal itself; status / generated metadata
 *  surround it so the dashboard can render "proposed 3 hours ago,
 *  waiting for your call" and the api can refuse duplicate
 *  approval calls. */
export const channelTakeoverProposalEnvelopeSchema = z.object({
    status: channelTakeoverStatusSchema,
    proposal: channelTakeoverProposalSchema,
    generatedAt: z.string().datetime(),
    /** ISO timestamp the founder accepted (status='approved') OR
     *  discarded (status='discarded'). Null for status='proposed'. */
    decidedAt: z.string().datetime().nullable(),
    /** Model id used to generate the proposal. Helps with cost
     *  attribution + lets us re-evaluate a proposal if we change
     *  models. */
    llmModelId: z.string().nullable(),
    /** Approximate cost of the generation in cents — same format
     *  brand_content_plans uses. */
    costCents: z.number().int().min(0),
});
//# sourceMappingURL=channel-takeover-proposal.js.map